/**
 * @file        src/components/BeyondLogo.jsx
 * @description Full Beyond Destination brand logo, recreated as a scalable inline
 *              SVG from the approved artwork: the travel doodles (mustard plane,
 *              coral passport, teal/coral hot-air balloon, teal camera) around the
 *              cursive teal "Beyond", the airplane flight-path swash into a jet,
 *              bold "Destination", and the "Flight | Hotel | Holidays | Visa"
 *              tagline.
 *
 *              Colour: the doodles keep their fixed brand colours and "Beyond" is
 *              always teal (--nh-sky). The flight path, "Destination" and tagline
 *              use `currentColor`, so the lockup stays legible on any surface —
 *              white on the dark hero/nav, ink on light pages — set via a text-*
 *              class on the caller. Inline SVG (not <img>) so the Yellowtail /
 *              Anton / Manrope webfonts render.
 * @author      Kinetika Labs
 * @created     2026-07-22
 * @changelog
 *   - 2026-07-22 Claude — Initial. Full-logo recreation (doodles + lockup).
 */
import React from 'react';

const TEAL = 'rgb(var(--nh-sky))';
const CORAL = '#E8687D';
const CORAL_D = '#C64F63';
const CORAL_L = '#F4B3BD';
const MUSTARD = '#F0A62A';
const CAM = '#6FC7D6';
const CAM_D = '#2C8FA6';

// Material "flight" glyph (24×24) — reused for the mustard doodle plane and the
// small black jet at the end of the flight path.
const PLANE = 'M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z';

export default function BeyondLogo({ className = 'h-14 w-auto', showTagline = true }) {
  return (
    <svg
      className={className}
      viewBox="0 0 660 380"
      fill="none"
      role="img"
      aria-label="Beyond Destination — Flight, Hotel, Holidays, Visa"
      style={{ overflow: 'visible' }}
    >
      {/* ---- doodle: mustard plane (top-left) ---- */}
      <g transform="translate(6 6) scale(2.6) rotate(-28 12 12)">
        <path d={PLANE} fill={MUSTARD} />
      </g>

      {/* ---- doodle: passport (upper middle) ---- */}
      <g transform="rotate(-7 268 108)">
        <rect x="234" y="58" width="70" height="100" rx="7" fill={CORAL} />
        <rect x="234" y="58" width="11" height="100" rx="4" fill={CORAL_D} />
        <rect x="256" y="74" width="40" height="11" rx="2" fill={CORAL_D} />
        <circle cx="272" cy="120" r="17" fill="none" stroke={CORAL_L} strokeWidth="2.4" />
        <path d="M272 103 v34 M255 120 h34 M259 111 q13 9 26 0 M259 129 q13 -9 26 0" stroke={CORAL_L} strokeWidth="1.6" fill="none" />
      </g>

      {/* ---- doodle: hot-air balloon (top-right) ---- */}
      <g transform="translate(0 -4)">
        <clipPath id="bdBalloon"><circle cx="576" cy="74" r="44" /></clipPath>
        <g clipPath="url(#bdBalloon)">
          <rect x="530" y="28" width="92" height="92" fill={CAM} />
          <rect x="548" y="28" width="15" height="92" fill={CORAL} />
          <rect x="588" y="28" width="15" height="92" fill={CORAL} />
          <rect x="568" y="28" width="6" height="92" fill="#fff" opacity="0.35" />
        </g>
        <path d="M566 116 h20 l-10 12 z" fill={MUSTARD} />
        <path d="M568 120 l-4 16 M584 120 l4 16" stroke={CAM_D} strokeWidth="2" />
        <rect x="562" y="132" width="28" height="18" rx="3" fill={MUSTARD} />
        <path d="M566 132 v18 M576 132 v18 M586 132 v18" stroke="#C9861E" strokeWidth="1.4" />
      </g>

      {/* ---- doodle: camera (bottom-right) ---- */}
      <g>
        <rect x="596" y="292" width="26" height="14" rx="3" fill={CAM} />
        <rect x="512" y="300" width="120" height="80" rx="12" fill={CAM} />
        <rect x="512" y="328" width="120" height="22" fill={MUSTARD} />
        <rect x="528" y="312" width="16" height="9" rx="2" fill="#fff" opacity="0.7" />
        <circle cx="574" cy="342" r="24" fill={CAM_D} />
        <circle cx="574" cy="342" r="14" fill={CAM} />
        <circle cx="574" cy="342" r="6" fill={CAM_D} />
        <circle cx="568" cy="336" r="3" fill="#fff" opacity="0.8" />
      </g>

      {/* ---- cursive "Beyond" ---- */}
      <text x="92" y="258" fill={TEAL} fontFamily="'Yellowtail', cursive" fontSize="150" transform="rotate(-3 92 258)">
        Beyond
      </text>

      {/* ---- flight path + jet (currentColor) ---- */}
      <path
        d="M150 292 C 126 288 124 260 148 262 C 162 263 160 278 176 278 L 452 232"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <g transform="translate(436 206) scale(1.9) rotate(48 12 12)">
        <path d={PLANE} fill="currentColor" />
      </g>

      {/* ---- bold "Destination" ---- */}
      <text x="196" y="330" fill="currentColor" fontFamily="'Anton', sans-serif" fontSize="66" letterSpacing="1">
        Destination
      </text>

      {showTagline && (
        <text x="200" y="372" fill="currentColor" fontFamily="'Manrope', sans-serif" fontSize="23" fontWeight="800" letterSpacing="1">
          Flight | Hotel | Holidays | Visa
        </text>
      )}
    </svg>
  );
}
